import React from "react";

/**
 * Universal video renderer — supports YouTube/Vimeo embeds AND direct upload/CDN URLs.
 *
 * Aspect ratio is locked by an outer wrapper (`aspect-video` = 16:9 by default) so the layout
 * NEVER shifts based on YouTube's own iframe intrinsic size. The iframe fills 100%/100% of the
 * wrapper via absolute positioning, which is exactly the pattern YouTube's own docs recommend.
 *
 * Pass `aspect` to override (e.g. "aspect-[4/3]" or "aspect-square"). The wrapper is `w-full` so
 * responsiveness is automatic — width follows the parent container.
 *
 *   <VideoEmbed src={anyUrl} poster={maybe} aspect="aspect-video" />
 *
 * Detection order:
 *   1. YouTube (youtube.com/watch, youtu.be, /embed/, /shorts/) → <iframe>
 *   2. Vimeo (vimeo.com/{id}) → <iframe>
 *   3. Native video file (.mp4/.webm/.mov OR /api/media/{uuid}) → <video>
 *   4. Fallback: renders the poster (if any) with a play icon
 */

function parseYouTubeId(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    // youtu.be/{id}
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1).split(/[?/]/)[0] || null;
    if (!u.hostname.includes("youtube.com") && !u.hostname.includes("youtube-nocookie.com")) return null;
    // /embed/{id} or /shorts/{id} or /v/{id}
    const parts = u.pathname.split("/").filter(Boolean);
    if (["embed", "shorts", "v"].includes(parts[0])) return parts[1] || null;
    // /watch?v={id}
    return u.searchParams.get("v");
  } catch {
    return null;
  }
}

function parseVimeoId(url) {
  if (!url || !url.includes("vimeo.com")) return null;
  try {
    const u = new URL(url);
    const id = u.pathname.split("/").filter(Boolean).pop();
    return /^\d+$/.test(id || "") ? id : null;
  } catch {
    return null;
  }
}

function isNativeVideo(url) {
  if (!url) return false;
  if (url.startsWith("/api/media/")) return true;
  const lower = url.split("?")[0].toLowerCase();
  return /\.(mp4|webm|mov|m4v|ogv)$/.test(lower);
}

function resolveSrc(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/media/")) return `${process.env.REACT_APP_BACKEND_URL || ""}${u}`;
  return u;
}

export default function VideoEmbed({
  src,
  poster,
  title = "video",
  aspect = "aspect-video",
  className = "",
  autoplay = false,
  loop = false,
  muted = false,
  controls = true,
}) {
  const ytId = parseYouTubeId(src);
  const vmId = parseVimeoId(src);
  const native = isNativeVideo(src);

  const wrapperCls = `relative w-full ${aspect} overflow-hidden bg-black ${className}`;

  if (ytId) {
    const params = new URLSearchParams({
      rel: "0",
      modestbranding: "1",
      playsinline: "1",
      iv_load_policy: "3",   // no annotations
      cc_load_policy: "0",   // no captions by default
      fs: "1",
      disablekb: "1",
      showinfo: "0",
    });
    if (autoplay) { params.set("autoplay", "1"); params.set("mute", "1"); }
    if (loop) { params.set("loop", "1"); params.set("playlist", ytId); }
    return (
      <div className={wrapperCls} data-testid="video-embed-youtube">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${ytId}?${params.toString()}`}
          title={title}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 w-full h-full border-0"
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    );
  }

  if (vmId) {
    return (
      <div className={wrapperCls} data-testid="video-embed-vimeo">
        <iframe
          src={`https://player.vimeo.com/video/${vmId}${autoplay ? "?autoplay=1&muted=1" : ""}`}
          title={title}
          loading="lazy"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full border-0"
        />
      </div>
    );
  }

  if (native) {
    return (
      <div className={wrapperCls} data-testid="video-embed-native">
        <video
          src={resolveSrc(src)}
          poster={poster ? resolveSrc(poster) : undefined}
          controls={controls}
          autoPlay={autoplay}
          loop={loop}
          muted={muted}
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>
    );
  }

  // Fallback: poster only (or blank)
  return (
    <div className={wrapperCls} data-testid="video-embed-fallback">
      {poster && <img src={resolveSrc(poster)} alt={title} className="absolute inset-0 w-full h-full object-cover" />}
    </div>
  );
}
